import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { collection, doc, getDocs, getDoc, setDoc, query, where, addDoc, deleteDoc, orderBy } from "firebase/firestore";
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import Papa from 'papaparse';
import uniqid from 'uniqid';
import ReactLoading from 'react-loading';
import fetch from 'node-fetch';
import moment from "moment";

import { db } from "../../../firebase";

import { RoundIconButton, Button } from "../components/Buttons.jsx";
import { InputSimple, SelectSimple, TextArea, DateTimePicker } from "../components/Inputs.jsx";
import Dropzone from "../../../components/Dropzone.jsx";
import Modal from "../../../components/Modal.jsx";

import { MdAddAlarm } from "react-icons/md";
import { FaCheckCircle } from "react-icons/fa";
import { TiDeleteOutline } from "react-icons/ti";
import { MdOutlineAddCircleOutline } from "react-icons/md";
import { HiOutlineTrash } from "react-icons/hi";
import { MdOutlineEditCalendar } from "react-icons/md";

export const Event = () => {
    const { eventId } = useParams();
    const navigate = useNavigate();
    const [event, setEvent] = useState(false);
    const [init, setInit] = useState(true);
    const [saved, setSaved] = useState(false);
    const [filters, setFilters] = useState([]);
    const [searchEventUsers, setSearchEventUsers] = useState("");
    const [eventUsers, setEventUsers] = useState([]); // [ { email: "", type: "", date: "" } ]
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [schedules, setSchedules] = useState([]);
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [newScheduleDate, setNewScheduleDate] = useState(new Date());
    const [loadingSchedules, setLoadingSchedules] = useState(false);
    const [showDownloadOptions, setShowDownloadOptions] = useState(false);
    const [downloadMode, setDownloadMode] = useState("all");
    const [emailsToFilter, setEmailsToFilter] = useState("");
    const [includeLoginLinks, setIncludeLoginLinks] = useState(false);
    const updateTimer = useRef(false);

    const getFilters = () => {
        // Get filters from firestore
        const filtersRef = collection(db, "filters");
        getDocs(filtersRef).then((querySnapshot) => {
            let filterList = [];
            querySnapshot.forEach((doc) => {
                let filter = doc.data();
                filter.id = doc.id;
                filterList.push(filter);
            });
            setFilters(filterList);
        }
        ).catch((error) => {
            console.log("Error getting documents: ", error);
        });
    }

    useEffect(() => {
        // Get user from firestore
        const docRef = doc(db, "events", eventId);
        getDoc(docRef).then((doc) => {
            if (doc.exists()) {
                let eventData = doc.data();
                eventData.id = doc.id;
                
                // console.log(eventData.slots && eventData.slots, eventData.slots && Object.keys(eventData.slots));
                if(eventData.slots && Object.keys(eventData.slots).length > 0) {
                    let newSlots = {};
                    // Reorder eventData.slots (format { slotId: { begin: Date, end: Date, creationDate: Date, index: Number } }) by index value, preserve original keys
                    Object.keys(eventData.slots).sort((a, b) => {
                        return eventData.slots[a].index - eventData.slots[b].index;
                    }).forEach((slotId) => {
                        newSlots[slotId] = eventData.slots[slotId];
                    });
                    eventData.slots = newSlots;
                }
                setEvent(eventData);
                getFilters();
                // console.log("Document data:", doc.data());
            } else {
                console.log("No such document!");
            }
        }).catch((error) => {
            console.log("Error getting document:", error);
        });
    }, [eventId]);

    useEffect(() => {
        if(init === false) {
            if(updateTimer.current) clearTimeout(updateTimer.current);
            updateTimer.current = setTimeout(() => {
                const docRef = doc(db, "events", eventId);
                setDoc(docRef, event);
                setSaved(true);
                setTimeout(() => {
                    setSaved(false);
                }, 1000);
            }, 1000);
        } else if(event && event.id) {
            setInit(false);
        }

        listSchedules();
        // eslint-disable-next-line
    }, [event]);

    const validateCSVFormat = (csvData) => {
        const validTypes = ["Startup", "Venture Capital", "Agency / consulting", "Corporate", "Ecosystem", "Policy Maker"];
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const errors = [];
        const validRows = [];

        // Check if required headers exist
        if (!csvData.meta.fields || !csvData.meta.fields.includes('email') || !csvData.meta.fields.includes('type')) {
            errors.push("CSV must have 'email' and 'type' columns. Expected format: email,type");
            return { isValid: false, errors, validRows: [] };
        }

        // Validate each row
        csvData.data.forEach((row, index) => {
            const rowErrors = [];
            const lineNumber = index + 2; // +2 because index starts at 0 and we have header row

            // Check if email exists and is valid
            if (!row.email || row.email.trim() === '') {
                rowErrors.push(`Line ${lineNumber}: Email is required`);
            } else if (!emailRegex.test(row.email.trim())) {
                rowErrors.push(`Line ${lineNumber}: Invalid email format (${row.email})`);
            }

            // Check if type exists and is valid
            if (!row.type || row.type.trim() === '') {
                rowErrors.push(`Line ${lineNumber}: Type is required`);
            } else if (!validTypes.includes(row.type.trim())) {
                rowErrors.push(`Line ${lineNumber}: Invalid type "${row.type}". Must be one of: ${validTypes.join(', ')}`);
            }

            if (rowErrors.length > 0) {
                errors.push(...rowErrors);
            } else {
                validRows.push({
                    email: row.email.trim(),
                    type: row.type.trim()
                });
            }
        });

        return {
            isValid: errors.length === 0,
            errors,
            validRows
        };
    };

    const handleUsers = (files) => {
        for(const file of files) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const text = e.target.result;
                const results = Papa.parse(text, { header: true, skipEmptyLines: true });
                
                // Validate CSV format
                const validation = validateCSVFormat(results);
                
                if (!validation.isValid) {
                    const errorMessage = "CSV format validation failed:\n\n" + validation.errors.join('\n');
                    alert(errorMessage);
                    return;
                }
                
                // Show preview and ask for confirmation
                const previewMessage = `Found ${validation.validRows.length} valid users to import:\n\n` +
                    validation.validRows.slice(0, 5).map(row => `${row.email} (${row.type})`).join('\n') +
                    (validation.validRows.length > 5 ? `\n... and ${validation.validRows.length - 5} more` : '') +
                    '\n\nProceed with import?';
                
                if (!window.confirm(previewMessage)) {
                    return;
                }
                
                // Import validated users
                let countUsers = 0;
                validation.validRows.forEach((user) => {
                    setDoc(doc(db, "events", eventId, "users", user.email), {
                        email: user.email,
                        type: user.type,
                        date: new Date(),
                    });
                    countUsers++;
                });
                
                alert(`${countUsers} Users added / updated successfully`);
            };
            reader.readAsText(file);
        };
    };

    const handleSearchUsers = (searchTerm) => {
        // If searchEventUsers is email search
        if(searchTerm.length >= 3) {
            const usersRef = collection(db, 'events', eventId, 'users');
            // search email begins with searchEventUsers
            const q = query(usersRef, where('email', '>=', searchTerm), where('email', '<=', searchTerm + '\uf8ff'));
            getDocs(q).then((querySnapshot) => {
                let userList = [];
                querySnapshot.forEach((docRef) => {
                    let userItem = docRef.data();
                    userItem.id = docRef.id;
                    userList.push(userItem);
                });
                setEventUsers(userList);
            });
        }
    }

    const handleDownloadUsers = async () => {
        setDownloadLoading(true);
        const adminConfig = await getDoc(doc(db, "configs", "admin"));
        if (!adminConfig.exists()) {
            console.error("Admin config not found");
            return;
        }
        const adminConfigData = adminConfig.data();

        // Construire l'URL avec les paramètres
        let url = `${window.fdVariables.apiBaseurl}/admin/authlinks/${eventId}`;
        const params = new URLSearchParams();

        // Ajouter le paramètre includeLoginLinks
        params.append('includeLoginLinks', includeLoginLinks.toString());

        // Ajouter le paramètre emails si mode filter
        if (downloadMode === "filter" && emailsToFilter.trim()) {
            // Parser les emails du textarea (un par ligne)
            const emails = emailsToFilter
                .split('\n')
                .map(email => email.trim())
                .filter(email => email.length > 0);

            if (emails.length > 0) {
                params.append('emails', emails.join(','));
            }
        }

        url += `?${params.toString()}`;

        const response = await fetch(url, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "authorization": `Bearer ${adminConfigData.key}`,
            },
        });
        const data = await response.json();

        if (data.success && data.userList) {
            const csvContent = Papa.unparse(data.userList);
            const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
            const link = document.createElement("a");
            const url = URL.createObjectURL(blob);
            link.setAttribute("href", url);
            link.setAttribute("download", `event_users_${eventId}.csv`);
            link.style.visibility = "hidden";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setDownloadLoading(false);
            setShowDownloadOptions(false); // Fermer les options après téléchargement
        } else {
            console.error("Failed to fetch user data or no users found.");
            setDownloadLoading(false);
        }
    };

    const handleDeleteUser = (userId) => {
        console.log("Delete user", userId);
        let confirm = window.confirm("Are you sure you want to delete this user?");
        if(confirm === false) return;

        const userRef = doc(db, 'events', eventId, 'users', userId);
        deleteDoc(userRef).then(() => {
            console.log("Document successfully deleted!");
            handleSearchUsers(searchEventUsers);
        }).catch((error) => {
            console.error("Error removing document: ", error);
        });
    }

    const handleAddSlot = () => {
        console.log("Add slot");
        const slots = event.slots ? event.slots : {};
        slots[uniqid()] = {
            begin: new Date(),
            end: new Date(),
            creationDate: new Date(),
            index: Object.keys(slots).length,
        };
        setEvent({ ...event, slots });
    }

    const handleDeleteSlot = (slotId) => {
        console.log("Delete slot", slotId);
        let confirm = window.confirm("Are you sure you want to delete this slot?");
        if(confirm === false) return;

        const slots = event.slots;
        delete slots[slotId];

        // Update index
        let index = 0;
        for(const slotId in slots) {
            slots[slotId].index = index;
            index++;
        }

        setEvent({ ...event, slots });
    }

    const handleReorderSlots = (result) => {
        console.log("Reorder slots", result);
        if (!result.destination) return;
        
        const slots = event.slots;
        const items = Object.entries(slots);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        let index = 0;
        let newSlots = {};
        items.forEach(([slotId, slot]) => {
            slot.index = index;
            newSlots[slotId] = slot;
            index++;
        });

        setEvent({ ...event, slots: newSlots });

    }

    const handleChangeFilters = (filterId) => {
        let eventFilters = [];
        if(!event.filters) {
            eventFilters = [];
        } else {
            eventFilters = event.filters;
        }
        if(eventFilters.includes(filterId)) {
            eventFilters = eventFilters.filter((f) => f !== filterId);
        } else {
            eventFilters.push(filterId);
        }
        setEvent({ ...event, filters: eventFilters });
    }

    const listSchedules = () => {
        fetch(`${window.fdVariables.apiBaseurl}/schedules/${event.id}`, {
            method: "GET",
        }).then((response) => {
            if(response.ok) {
                return response.json();
            } else {
                throw new Error("Failed to fetch schedules");
            }
        }).then((data) => {
            if(data.success) {
                setSchedules(data.data);
            } else {
                console.error("Failed to fetch schedules", data.error);
            }
        }).catch((error) => {
            console.error("Error fetching schedules:", error);
        });
    }

    const handleCreateSchedule = () => {
        setLoadingSchedules(true);
        console.log("Create schedule", newScheduleDate.format("YYYY-MM-DD HH:mm:ss"));
        fetch(`${window.fdVariables.apiBaseurl}/schedules/${event.id}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                schedule: newScheduleDate.format("YYYY-MM-DD HH:mm:ss"),
            }),
        }).then((response) => {
            if(response.ok) {
                return response.json();
            } else {
                throw new Error("Failed to create schedule");
            }
        }).then((data) => {
            setLoadingSchedules(false);
            if(data.success) {
                setShowScheduleModal(false);
                listSchedules();
            } else {
                console.error("Failed to create schedule", data.error);
            }
        }).catch((error) => {
            setLoadingSchedules(false);
            console.error("Error creating schedule:", error);
        });
    }

    const handleDeleteSchedule = (jobName) => {
        console.log("Delete schedule", jobName);
        let confirm = window.confirm("Are you sure you want to delete this schedule?");
        if(confirm === false) return;

        fetch(`${window.fdVariables.apiBaseurl}/schedules/${event.id}`, {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                jobName: jobName,
            }),
        }).then((response) => {
            if(response.ok) {
                return response.json();
            } else {
                throw new Error("Failed to delete schedule");
            }
        }).then((data) => {
            if(data.success) {
                listSchedules();
            } else {
                console.error("Failed to delete schedule", data.error);
            }
        }).catch((error) => {
            console.error("Error deleting schedule:", error);
        });
    }

    return <div className="pb-4">
        <div className="flex items-center text-5xl">
            <h1 className="text-5xl">{event.name}</h1>
            <div className={`transition-all duration-1000 ${saved ? 'opacity-100' : 'opacity-0'}`}><FaCheckCircle className="text-green-700 ml-2" /></div>
        </div>
        <div className="mt-5 grid grid-cols-3 gap-2">
            <div>
                <label>Event Name</label>
                <InputSimple label="Event name" value={event.name} onChange={(e) => setEvent({ ...event, name: e.target.value })} />
            </div>
            <div>
                <label>Event Dominant</label>
                <SelectSimple label="Dominant" name="dominant" value={event && event.dominant ? event.dominant : 'Venture Capital'} options={[
                    { value: "standard", label: "Standard User" },
                    { value: "Agency / consulting", label: "Agency / consulting" },
                    { value: "Corporate", label: "Corporate" },
                    { value: "Ecosystem", label: "Ecosystem" },
                    { value: "Policy Maker", label: "Policy Maker" },
                    { value: "Startup", label: "Startup" },
                    { value: "Venture Capital", label: "Venture Capital" },
                ]} onChange={(e) => {
                    console.log("Change dominant");
                    setEvent({ ...event, dominant: e.target.value });
                }} />
                <small>Event dominant can access startup list & select them to wishlist</small>
            </div>
            <div>
                <label>Event Dominated</label>
                <SelectSimple label="Dominated" name="dominated" value={event && event.dominated ? event.dominated : 'Startup'} options={[
                    { value: "standard", label: "Standard User" },
                    { value: "Agency / consulting", label: "Agency / consulting" },
                    { value: "Corporate", label: "Corporate" },
                    { value: "Ecosystem", label: "Ecosystem" },
                    { value: "Policy Maker", label: "Policy Maker" },
                    { value: "Startup", label: "Startup" },
                    { value: "Venture Capital", label: "Venture Capital" },
                ]} onChange={(e) => {
                    console.log("Change dominant");
                    setEvent({ ...event, dominated: e.target.value });
                }} />
                <small>Event dominated are selected by dominant & can just see schedule</small>
            </div>
        </div>
        <div>
            <label>Event Description</label>
            <TextArea label="Description" value={event.description} onChange={(e) => setEvent({ ...event, description: e.target.value })} />
        </div>
        <div className="mt-5 grid grid-cols-2 gap-2">
            <div>
                <label>Place name</label>
                <InputSimple label="Place name" value={event.place} onChange={(e) => setEvent({ ...event, place: e.target.value })} />
            </div>
            <div>
                <label>Place address</label>
                <InputSimple label="Place address" value={event.address} onChange={(e) => setEvent({ ...event, address: e.target.value })} />
            </div>
        </div>
        <div className="mt-5 grid grid-cols-2 gap-2">
            <div>
                <label>Event Start Date</label>
                <DateTimePicker label="Start Date" value={event.startDate} onChange={(date) => {
                    if(typeof date === 'object') {
                        setEvent({ ...event, startDate: date.toDate() })
                    }
                }} dateFormat={'YYYY-MM-DD'} timeFormat={"HH:mm"} />
            </div>
            <div>
                <label>Event End Date</label>
                <DateTimePicker label="End Date" value={event.endDate} onChange={(date) => {
                    if(typeof date === 'object') {
                        setEvent({ ...event, endDate: date.toDate() })
                    }
                }} dateFormat={'YYYY-MM-DD'} timeFormat={"HH:mm"} />
            </div>
            <div>
                <label>Wishes End Date</label>
                <DateTimePicker label="Wish Date" value={event.wishEndDate} onChange={(date) => {
                    if(typeof date === 'object') {
                        setEvent({ ...event, wishEndDate: date.toDate() })
                    }
                }} dateFormat={'YYYY-MM-DD'} timeFormat={"HH:mm"} />
                <small>End date to make wishes. Calendar will be displayed for everybody at this time</small>
            </div>
            <div>
                <div>
                    <label>Hide Unvalidated Meetings</label>
                    <div className="flex items-center mt-1">
                        <input 
                            type="checkbox" 
                            className="mr-2 h-5 w-5" 
                            checked={event.hideUnvalidatedMeetings || false} 
                            onChange={(e) => setEvent({ ...event, hideUnvalidatedMeetings: e.target.checked })} 
                        />
                        <small>When checked, unvalidated meetings will not appear in generated schedules</small>
                    </div>
                </div>
            </div>
        </div>
        <div className="mt-5">
            <h2 className="text-2xl">Filters</h2>
            {/* By default, if not event.filters, all filters are available. Either, select only ids in event.filters. Filters can be selected with checkboxes */}
            <div className="">
                {filters.map((filter) => {
                    return <div key={filter.id}>
                        <input className="mr-2" type="checkbox" checked={event.filters ? event.filters.includes(filter.id) : false} onChange={() => handleChangeFilters(filter.id)} />
                        <label>{filter.name}</label>
                    </div>;
                })}
            </div>
        </div>
        <div className="mt-5">
            <div className="flex items-center">
                <h2 className="text-2xl">Event Users</h2>
                <a href="https://storage.googleapis.com/public-fd/samples/import.csv" target="_blank" rel="noreferrer" className="ml-2">(Download sample csv)</a>
            </div>
            <Dropzone
                placeholder="Drag and drop your csv file here or click to browse"
                handleFile={handleUsers}
                name="users"
                accept={{
                    "text/csv": [],
                }}
                style={{
                    maxHeight: "100px",
                }}
            />
            <div className="text-sm text-gray-600 mt-2">
                <strong>Required format:</strong> CSV with "email" and "type" columns. 
                Valid types: Startup, Venture Capital, Agency / consulting, Corporate, Ecosystem, Policy Maker
            </div>
            <InputSimple placeholder="Search users" className="mt-2" label="Search users" value={searchEventUsers} onChange={(e) => {
                setSearchEventUsers(e.target.value)
                handleSearchUsers(e.target.value);
            }} />
            <div className="mt-2">
                <button 
                    onClick={() => setShowDownloadOptions(!showDownloadOptions)}
                >
                    Download users CSV
                </button>
                
                {showDownloadOptions && (
                    <div className="mt-4 p-4 border border-gray-300 rounded-lg bg-gray-50">
                        <div className="mb-4">
                            <h3 className="text-sm font-semibold mb-2 text-gray-700">User Selection</h3>
                            <div className="flex items-center mb-2">
                                <input
                                    type="radio"
                                    id="all-users"
                                    name="downloadMode"
                                    value="all"
                                    checked={downloadMode === "all"}
                                    onChange={(e) => setDownloadMode(e.target.value)}
                                    className="mr-2"
                                />
                                <label htmlFor="all-users" className="text-sm font-medium">All Users</label>
                            </div>
                            <div className="flex items-center">
                                <input
                                    type="radio"
                                    id="filter-users"
                                    name="downloadMode"
                                    value="filter"
                                    checked={downloadMode === "filter"}
                                    onChange={(e) => setDownloadMode(e.target.value)}
                                    className="mr-2"
                                />
                                <label htmlFor="filter-users" className="text-sm font-medium">Filter users</label>
                            </div>
                        </div>

                        {downloadMode === "filter" && (
                            <div className="mb-4">
                                <label htmlFor="emails-textarea" className="block text-sm font-medium text-gray-700 mb-2">
                                    Enter emails (one per line):
                                </label>
                                <textarea
                                    id="emails-textarea"
                                    value={emailsToFilter}
                                    onChange={(e) => setEmailsToFilter(e.target.value)}
                                    placeholder="user1@example.com&#10;user2@example.com&#10;user3@example.com"
                                    rows={6}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                        )}

                        <div className="mb-4 border-t pt-4">
                            <h3 className="text-sm font-semibold mb-2 text-gray-700">Login Links</h3>
                            <div className="flex items-center mb-2">
                                <input
                                    type="radio"
                                    id="without-login-link"
                                    name="loginLinkMode"
                                    value="false"
                                    checked={includeLoginLinks === false}
                                    onChange={(e) => setIncludeLoginLinks(e.target.value === "true")}
                                    className="mr-2"
                                />
                                <label htmlFor="without-login-link" className="text-sm font-medium">Without login link</label>
                            </div>
                            <div className="flex items-center">
                                <input
                                    type="radio"
                                    id="with-login-link"
                                    name="loginLinkMode"
                                    value="true"
                                    checked={includeLoginLinks === true}
                                    onChange={(e) => setIncludeLoginLinks(e.target.value === "true")}
                                    className="mr-2"
                                />
                                <label htmlFor="with-login-link" className="text-sm font-medium">With login link</label>
                            </div>
                        </div>
                        
                        <div className="flex items-center justify-end space-x-2">
                            <button 
                                onClick={() => setShowDownloadOptions(false)}
                                className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded"
                            >
                                Cancel
                            </button>
                            {downloadLoading ? (
                                <div className="flex items-center bg-blue text-white font-bold py-2 px-4 rounded">
                                    <span style={{ marginRight: 5 }}>Downloading...</span>
                                    <ReactLoading type="spin" color="#ffffff" width={20} height={20} />
                                </div>
                            ) : (
                                <button 
                                    onClick={handleDownloadUsers}
                                    className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
                                >
                                    Download
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>
            <div className="mt-2">
                {eventUsers.map((user) => {
                    return <div key={user.id} className="bg-white shadow-md p-5 rounded-lg mt-5">
                        <div className="flex items-center justify-between">
                            <div><span className="text-lg">{user.email}</span> <small className="ml-2">({user.type})</small></div>
                            <div>
                                <RoundIconButton onClick={() => handleDeleteUser(user.id)}>
                                    <TiDeleteOutline className="text-lg" />
                                </RoundIconButton>
                            </div>
                        </div>
                    </div>;
                })}
            </div>
        </div>
        <div className="mt-5">
            <div className="flex items-center">
                <h2 className="text-2xl mr-2">
                    Meeting Slots
                </h2>
                <RoundIconButton onClick={handleAddSlot}>
                    <MdOutlineAddCircleOutline size={24} />
                </RoundIconButton>
                <RoundIconButton onClick={() => navigate(`/events/${eventId}/meetings`)}>
                    <MdOutlineEditCalendar size={24} />
                </RoundIconButton>
            </div>
            <div>
                <DragDropContext onDragEnd={handleReorderSlots}>
                    <Droppable droppableId="droppable">
                        {(provided) => (
                            <div {...provided.droppableProps} ref={provided.innerRef}>
                                {Object.keys(event.slots ? event.slots : {}).map((slotId, index) => (
                                    <Draggable key={slotId} draggableId={slotId} index={index}>
                                        {(provided) => (
                                            <div
                                                className="bg-white shadow-md p-5 rounded-lg mt-5"
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                            >
                                                <div className="grid grid-cols-3 gap-2">
                                                    <div className="col-span-3 flex items-center">
                                                        <RoundIconButton onClick={() => handleDeleteSlot(slotId)}>
                                                            <HiOutlineTrash size={24} color="red" />
                                                        </RoundIconButton>
                                                        <span className="mr-1">Slot id : {slotId}</span>
                                                    </div>
                                                    <div>
                                                        <label>Slot name</label>
                                                        <InputSimple label="Name" value={event.slots[slotId].name} onChange={(e) => {
                                                            let slots = event.slots;
                                                            slots[slotId].name = e.target.value;
                                                            setEvent({ ...event, slots });
                                                        }} />
                                                    </div>
                                                    <div>
                                                        <label>Slot Begin</label>
                                                        <DateTimePicker label="Begin" value={event.slots[slotId].begin} onChange={(date) => {
                                                            let slots = event.slots;
                                                            // Check if date chain is moment object
                                                            if(typeof date === 'object') {
                                                                console.log(date)
                                                                slots[slotId].begin = date.toDate();
                                                                setEvent({ ...event, slots });
                                                            }
                                                        }} dateFormat={'YYYY-MM-DD'} timeFormat={"HH:mm"} />
                                                    </div>
                                                    <div>
                                                        <label>Slot End</label>
                                                        <DateTimePicker label="End" value={event.slots[slotId].end} onChange={(date) => {
                                                            let slots = event.slots;
                                                            if(typeof date === 'object') {
                                                                slots[slotId].end = date.toDate();
                                                                setEvent({ ...event, slots });
                                                            }
                                                        }} dateFormat={'YYYY-MM-DD'} timeFormat={"HH:mm"} />
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            </div>
        </div>
        <Modal isOpen={showScheduleModal} onClose={() => setShowScheduleModal(false)} title="Create Schedule" maxWidth="max-w-2xl">
            <p>Create a new schedule for unvalidated meetings in this event</p>
            <DateTimePicker label="Schedule Date" value={newScheduleDate} onChange={(date) => {
                setNewScheduleDate(date);
            }} dateFormat={'YYYY-MM-DD'} timeFormat={"HH:mm"} />
            <div onClick={handleCreateSchedule} className="mt-2 flex items-center justify-end">
                <Button type="button">
                    {loadingSchedules ? <ReactLoading type="spin" color="#fff" width={20} height={20} /> : `Create Schedule`}
                </Button>
            </div>
        </Modal>
        <div className="mt-5">
            <div className="flex items-center">
                <h2 className="text-2xl mr-2">Schedules</h2>
                <RoundIconButton onClick={() => setShowScheduleModal(true)}>
                    <MdOutlineAddCircleOutline size={24} />
                </RoundIconButton>
            </div>
            <div className="mt-2">
                {schedules.length > 0 && schedules.map((schedule) => {
                    return (
                        <div key={schedule.id} className="bg-white shadow-md p-5 rounded-lg mt-5 relative">
                            <h3 className="font-bold">{schedule.name.split('/').pop()}</h3>
                            <p>{schedule.description}</p>
                            <p>Date : {moment(schedule.scheduleTime.seconds * 1000).format("DD/MM/YYYY HH:mm")}</p>
                            <div className="absolute top-2 right-2">
                                <RoundIconButton onClick={() => handleDeleteSchedule(schedule.name)}>
                                    <HiOutlineTrash size={24} color="red" />
                                </RoundIconButton>
                            </div>
                        </div>
                    )
                })}
            </div>
        </div>
    </div>;
}

export const Events = () => {
    const navigate = useNavigate();
    const [events, setEvents] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const eventsPerPage = 10;

    useEffect(() => {
        // Get all events from firestore ordered by creation date (descending)
        const q = query(collection(db, "events"), orderBy("creationDate", "desc"));
        let eventList = [];
        getDocs(q).then((querySnapshot) => {
            querySnapshot.forEach((doc) => {
                let event = doc.data();
                event.id = doc.id;
                eventList.push(event);
                console.log(doc.id, " => ", doc.data());
            });
            setEvents(eventList);
        }).catch((error) => {
            console.log("Error getting documents: ", error);
        });
    }, []);

    const createEvent = () => {
        const eventName = window.prompt("Event name");
        if(eventName) {
            addDoc(collection(db, "events"), {
                name: eventName,
                dominant: "Venture Capital",
                dominated: "Startup",
                creationDate: new Date(),
            }).then((docRef) => {
                console.log("Document written with ID: ", docRef.id);
                navigate(`/events/${docRef.id}`);
            }).catch((error) => {
                console.error("Error adding document: ", error);
            });
        }
    }

    // Pagination logic
    const indexOfLastEvent = currentPage * eventsPerPage;
    const indexOfFirstEvent = indexOfLastEvent - eventsPerPage;
    const currentEvents = events.slice(indexOfFirstEvent, indexOfLastEvent);
    const totalPages = Math.ceil(events.length / eventsPerPage);

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    return <div>
        <div>
            <h1 className="text-5xl">Events</h1>
            <RoundIconButton onClick={createEvent} className="fixed top-5 right-5">
                <MdAddAlarm className="text-5xl" />
            </RoundIconButton>
        </div>
        <div className="mt-5">
            {/* Pagination info */}
            {events.length > 0 && (
                <div className="mb-4 text-gray-600">
                    Affichage de {indexOfFirstEvent + 1} à {Math.min(indexOfLastEvent, events.length)} sur {events.length} événements
                </div>
            )}

            {/* List events */}
            {currentEvents.map((event) => {
                return <div key={event.id} className="bg-white shadow-md p-5 rounded-lg mt-5 cursor-pointer" onClick={() => navigate(`/events/${event.id}`)}>
                    <div className="flex items-center justify-between">
                        <span className="text-2xl">{event.name}</span>
                        <div>
                            <RoundIconButton onClick={() => navigate(`/events/${event.id}`)}>
                                <MdAddAlarm className="text-2xl" />
                            </RoundIconButton>
                        </div>
                    </div>
                </div>;
            })}

            {/* Pagination controls */}
            {totalPages > 1 && (
                <div className="flex items-center justify-center gap-4 mt-8 mb-4">
                    <button
                        onClick={handlePreviousPage}
                        disabled={currentPage === 1}
                        className={`px-4 py-2 rounded-lg ${
                            currentPage === 1
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : 'bg-blue-500 text-white hover:bg-blue-600'
                        }`}
                    >
                        Précédent
                    </button>
                    <span className="text-gray-700">
                        Page {currentPage} sur {totalPages}
                    </span>
                    <button
                        onClick={handleNextPage}
                        disabled={currentPage === totalPages}
                        className={`px-4 py-2 rounded-lg ${
                            currentPage === totalPages
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : 'bg-blue-500 text-white hover:bg-blue-600'
                        }`}
                    >
                        Suivant
                    </button>
                </div>
            )}
        </div>
    </div>;
}